import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtTime, fmtGap, gruppoColor } from "@/lib/race";
import Podium from "@/components/Podium";

const TABS = [
  { key: "prove", label: "Prove (Sab)" },
  { key: "gara", label: "Gara (Dom)" },
  { key: "weekend", label: "Totale Weekend" },
];

const M_STATUS = {
  dns: { label: "DNS", cls: "text-[#FFB703]" },
  dnf: { label: "DNF", cls: "text-[#EF4444]" },
  dsq: { label: "DSQ", cls: "text-[#EF4444]" },
};

function MancheCell({ time, status }) {
  const st = M_STATUS[status];
  if (st) return <span className={`font-time text-xs font-bold ${st.cls}`}>{st.label}</span>;
  return <span className="font-time text-sm text-gray-300">{fmtTime(time)}</span>;
}

function CatBadge({ pilot, catColors }) {
  const cat = pilot.categoria || pilot.gruppo;
  const color = catColors[cat] || gruppoColor(pilot.gruppo);
  return (
    <span
      className="font-body-sport text-[10px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap"
      style={{ color, borderColor: `${color}55`, background: `${color}18` }}
    >
      {cat}
    </span>
  );
}

export default function Results() {
  const [tab, setTab] = useState("gara");

  const { data: results = [] } = useQuery({
    queryKey: ["results"],
    queryFn: async () => (await api.get("/results")).data,
    refetchInterval: 10000,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
  const catColors = Object.fromEntries(categories.map((c) => [c.nome, c.colore]));
  const ufficiale = settings?.race?.gara_finita === true;

  const sorted = [...results].sort((a, b) => {
    if (tab === "prove") return (a.prove_best ?? Infinity) - (b.prove_best ?? Infinity);
    if (tab === "gara") return (a.tempo_totale ?? Infinity) - (b.tempo_totale ?? Infinity);
    return (b.punti_totali ?? 0) - (a.punti_totali ?? 0);
  });

  const leader = sorted.find((r) => r.tempo_totale)?.tempo_totale;
  const garaPodium = results
    .filter((r) => r.posizione_assoluta)
    .sort((a, b) => a.posizione_assoluta - b.posizione_assoluta)
    .slice(0, 3)
    .map((r) => ({ pos: r.posizione_assoluta, title: r.pilota.cognome, points: r.punti_gara }));

  const thCls = "px-4 py-3 text-left font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase whitespace-nowrap";

  const cols = {
    prove: ["Pos", "#", "Pilota", "Categoria", "M1 Prove", "M2 Prove", "Pt Prove"],
    gara: ["Pos", "#", "Pilota", "Categoria", "M1 Gara", "M2 Gara", "Totale", "Gap", "Pt Gara"],
    weekend: ["Pos", "#", "Pilota", "Categoria", "Pt Prove", "Pt Gara", "Pt Extra", "Pt Totali"],
  };

  return (
    <div data-testid="results-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-racing font-bold text-xl sm:text-2xl uppercase tracking-wide text-white border-l-4 border-[#FF1E27] pl-3">
            Classifiche e Tempi
          </h2>
          <span
            data-testid="results-status-badge"
            className={`font-body-sport text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full border ${
              ufficiale
                ? "text-[#22C55E] border-[#22C55E]/50 bg-[#22C55E]/10"
                : "text-[#FFB703] border-[#FFB703]/50 bg-[#FFB703]/10"
            }`}
          >
            {ufficiale ? "Ufficiale" : "Ufficiosa"}
          </span>
        </div>
        <span className="flex items-center gap-2 font-body-sport text-[10px] font-bold tracking-widest text-[#FF1E27] uppercase">
          <span className="w-2 h-2 rounded-full bg-[#FF1E27] live-dot" />
          Aggiornamento live
        </span>
      </div>

      <div className="flex flex-wrap gap-1 rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] p-1 max-w-xl">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`results-filter-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex-1 min-w-[100px] rounded-lg px-3 py-2.5 min-h-[44px] font-body-sport font-bold text-[11px] tracking-widest uppercase transition-colors ${
              tab === t.key ? "bg-[#FF1E27] text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "gara" && garaPodium.length > 0 && (
        <Podium items={garaPodium} testidPrefix="pilot-podium" />
      )}

      <div
        className="w-full overflow-x-auto rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] scroll-smooth"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-[#3F3F4C] bg-[#121214]">
              {cols[tab].map((h) => (
                <th key={h} className={thCls}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const p = r.pilota;
              const gc = gruppoColor(p.gruppo);
              const posByTab = {
                prove: r.prove_best != null ? i + 1 : "—",
                gara: r.posizione_assoluta || "—",
              };
              const pos = posByTab[tab] ?? i + 1;
              const penalized = (r.penalita_gara || 0) > 0 || (r.penalita_punti || 0) !== 0;
              return (
                <tr
                  key={r.pilot_id}
                  data-testid={`result-row-${r.pilot_id}`}
                  className={`border-b border-[#242429] ${i % 2 === 0 ? "bg-[#1A1A1E]" : "bg-[#141416]"} hover:bg-[#242429] transition-colors`}
                >
                  <td className="px-4 py-3 font-racing font-black text-white">{pos}</td>
                  <td className="px-4 py-3 font-time font-bold" style={{ color: gc }}>{p.numero}</td>
                  <td className="px-4 py-3">
                    <p className="font-body-sport font-semibold text-white text-sm whitespace-nowrap">
                      {p.nome} {p.cognome}
                      {penalized && (
                        <span data-testid={`var-badge-${r.pilot_id}`} className="ml-2 font-body-sport text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#EF4444]/15 text-[#EF4444] border border-[#EF4444]/40">
                          VAR
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-gray-500 whitespace-nowrap">{p.vettura}</p>
                  </td>
                  <td className="px-4 py-3"><CatBadge pilot={p} catColors={catColors} /></td>
                  {tab === "prove" && (
                    <>
                      <td className="px-4 py-3"><MancheCell time={r.p1_time} status={r.p1_status} /></td>
                      <td className="px-4 py-3"><MancheCell time={r.p2_time} status={r.p2_status} /></td>
                      <td className="px-4 py-3 font-time font-bold text-sm text-white">
                        {r.punti_prove ?? 0}
                        {r.dettaglio_prove?.length > 0 && (
                          <span className="block text-[9px] font-normal text-[#FFB703]">{r.dettaglio_prove.join(" • ")}</span>
                        )}
                      </td>
                    </>
                  )}
                  {tab === "gara" && (
                    <>
                      <td className="px-4 py-3"><MancheCell time={r.g1_time} status={r.g1_status} /></td>
                      <td className="px-4 py-3"><MancheCell time={r.g2_time} status={r.g2_status} /></td>
                      <td className="px-4 py-3 font-time font-bold text-sm text-[#FFB703]">
                        {fmtTime(r.tempo_totale)}
                        {(r.penalita_gara || 0) > 0 && (
                          <span className="ml-1 text-[10px] text-[#EF4444]">+{r.penalita_gara}s</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-time text-sm text-gray-400">{fmtGap(leader, r.tempo_totale)}</td>
                      <td className="px-4 py-3 font-time font-bold text-sm text-white">{r.punti_gara ?? 0}</td>
                    </>
                  )}
                  {tab === "weekend" && (
                    <>
                      <td className="px-4 py-3 font-time text-sm text-gray-300">{r.punti_prove ?? 0}</td>
                      <td className="px-4 py-3 font-time text-sm text-gray-300">{r.punti_gara ?? 0}</td>
                      <td className={`px-4 py-3 font-time text-sm ${(r.punti_eventi ?? 0) >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                        {(r.punti_eventi ?? 0) >= 0 ? `+${r.punti_eventi ?? 0}` : r.punti_eventi}
                        {(r.eventi || []).length > 0 && (
                          <span className="block text-[9px] font-normal text-gray-500 leading-tight mt-0.5">
                            {r.eventi.map((e) => `${e.punti >= 0 ? "+" : ""}${e.punti} ${e.nome}`).join(" • ")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-time font-bold text-sm text-white">{r.punti_totali ?? 0}</td>
                    </>
                  )}
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-gray-500 text-sm py-12">
                  I tempi del weekend non sono ancora stati pubblicati.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
